import React from 'react';
import { ShiftBreak, ShiftLabel } from './types';
import FormattedDate from '../../../../../components/date/FormattedDate';
import { formatTime24h } from '../../../../../utils/dateUtils';
import { useNamespacedTranslation } from '../../../../../hooks/useNamespacedTranslation';
import FlairIcon from '../../../../../atomic/atoms/FlairIcon';

type EventTooltipProps = {
  startDate: Date;
  endDate: Date;
  breaks?: ShiftBreak[];
  employeeNotes?: string;
  shiftLabels?: ShiftLabel[];
};

export const EventTooltip: React.FC<EventTooltipProps> = ({
  startDate,
  endDate,
  breaks = [],
  employeeNotes,
  shiftLabels,
}) => {
  const t = useNamespacedTranslation('schedulerShifts.eventTooltip');
  return (
    <div className="p-2 custom-event-tooltip" style={{ minWidth: '16rem' }}>
      <TooltipSection
        icon={<FlairIcon icon="calendar" size="sm" />}
        value={
          <>
            <div>
              <FormattedDate day={startDate} format="day-with-weekday" />
            </div>
            <div>{`${formatTime24h(startDate)} - ${formatTime24h(
              endDate,
            )}`}</div>
          </>
        }
      />

      {breaks.length > 0 && (
        <TooltipSection
          label={t('breaks')}
          value={formatBreaks(breaks)}
          icon={<FlairIcon icon="cafe-outline" size="sm" />}
        />
      )}

      {shiftLabels && shiftLabels.length > 0 && (
        <TooltipSection
          label={t('labels')}
          value={formatLabels(shiftLabels)}
          icon={<FlairIcon icon="mic-outline" size="sm" />}
        />
      )}

      {!!employeeNotes && (
        <TooltipSection
          value={employeeNotes}
          icon={<FlairIcon icon="document-outline" size="sm" />}
        />
      )}
    </div>
  );
};

const formatBreaks = (breaks: ShiftBreak[]): string => {
  return breaks
    .map((breakItem) => {
      const start = formatTime24h(new Date(breakItem.startDate));
      const end = formatTime24h(new Date(breakItem.endDate));
      return `${start} - ${end}`;
    })
    .join(', ');
};

const formatLabels = (labels: ShiftLabel[]): string => {
  return labels.map((label) => label.name).join(', ');
};

type TooltipSectionProps = {
  label?: string;
  value: React.ReactNode;
  icon: React.ReactNode;
};

const TooltipSection: React.FC<TooltipSectionProps> = ({
  label,
  value,
  icon,
}) => {
  if (!value) {
    return null;
  }

  return (
    <div className="d-flex align-items-start mb-3 gap-3">
      {!!icon && (
        <div className="text-muted flex-shrink-0" style={{ marginTop: '2px' }}>
          {icon}
        </div>
      )}
      <div className="flex-grow-1 d-flex flex-column gap-1">
        {label && <div className="fw-semibold mb-1">{label}</div>}
        <div style={{ lineHeight: 1.4 }}>{value}</div>
      </div>
    </div>
  );
};
